import { useState, useEffect, useMemo } from "react";
import axiosInstance from "../../../../Utils/axiosInstance";
import { normalizeSegmentKey, businessPlanningCategoryMap, transformApiItems, DEFAULT_YEAR } from "./utils";

export const useCashInSegment = (location) => {
  const { dashboard, selectedProcess, segment, month: initialMonth, year: initialYear } = location.state || {};
  const segmentKey = normalizeSegmentKey(segment);

  const [periodOptions, setPeriodOptions] = useState([]);
  const [selectedPeriod, setSelectedPeriod] = useState(null);
  const [selectedYear, setSelectedYear] = useState(initialYear || DEFAULT_YEAR);
  
  const [rawItems, setRawItems] = useState([]);
  const [groupWiseItems, setGroupWiseItems] = useState([]);
  const [stateWiseItems, setStateWiseItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [classificationData, setClassificationData] = useState([]);
  const [classificationLoading, setClassificationLoading] = useState(false);
  const [classificationError, setClassificationError] = useState("");

  const { rows, monthColumns } = useMemo(() => transformApiItems(Array.isArray(rawItems) ? rawItems : []), [rawItems]);
  const { rows: groupRows, monthColumns: groupMonthColumns } = useMemo(() => transformApiItems(groupWiseItems), [groupWiseItems]);
  const { rows: stateRows, monthColumns: stateMonthColumns } = useMemo(() => transformApiItems(stateWiseItems), [stateWiseItems]);

  const transformedClassificationData = useMemo(() => {
    if (!classificationData.length) return [];
    const map = {};
    classificationData.forEach((monthObj) => {
      const month = monthObj.MonthYear;
      monthObj.Items.forEach((item) => {
        const key = item.Particulars;
        if (!map[key]) {
          map[key] = { Particulars: key };
        }
        Object.entries(item).forEach(([field, value]) => {
          if (field !== "Particulars" && field !== "Category as per BP") {
            map[key][`${month}_${field}`] = value;
          }
        });
      });
    });
    return Object.values(map);
  }, [classificationData]);

  const classificationMonths = useMemo(() => classificationData.map((x) => x.MonthYear), [classificationData]);

  const segmentColumns = useMemo(() => {
    if (!classificationData.length) return [];
    const firstItem = classificationData[0]?.Items?.[0];
    if (!firstItem) return [];
    return Object.keys(firstItem).filter((key) => key !== "Particulars" && key !== "Category as per BP");
  }, [classificationData]);

  const handleRowClick = (category) => {
    const isTotal = category.toLowerCase() === "grand total";
    if (isTotal) return;
    setSelectedCategory(prev => (prev === category ? null : category));
  };

  useEffect(() => {
    let ignore = false;
    const fetchPeriodOptions = async () => {
      try {
        const response = await axiosInstance.get("/eda3/eda3cashinperiods");
        if (!ignore && response?.data?.status) {
          const options = response.data.items.map((item) => ({
            value: item.value, label: item.label, months: item.value.split(","), year: item.year,
          }));
          setPeriodOptions(options);
          if (options.length > 0) setSelectedPeriod(options[0]);
        }
      } catch (err) {
        console.error("Failed to fetch cash-in period options", err);
      }
    };
    fetchPeriodOptions();
    return () => { ignore = true; };
  }, []);

  useEffect(() => {
    if (!location.state || !selectedPeriod) return;
    let ignore = false;
    const fetchBusinessPlanningSummary = async () => {
      setLoading(true); setError("");
      try {
        const response = await axiosInstance.post("/eda3/eda3cashincategorywise", {
          category: businessPlanningCategoryMap[segmentKey],
          period: selectedPeriod.months.join(","),
          Year: String(selectedYear),
        });

        if (!ignore && response?.data?.status) {
          const apiItems = response?.data?.items;
          if (Array.isArray(apiItems)) {
            setRawItems(apiItems); setGroupWiseItems([]); setStateWiseItems([]);
          } else if (segmentKey === "service" && apiItems) {
            setGroupWiseItems(apiItems["Group-wise Collection Summary"] || []);
            setStateWiseItems(apiItems["State-wise Collection Summary"] || []);
            setRawItems([]);
          } else {
            setRawItems([]); setGroupWiseItems([]); setStateWiseItems([]);
          }
        } else if (!ignore) {
          setError(response?.data?.message || "Failed to fetch summary.");
          setRawItems([]);
        }
      } catch (apiError) {
        if (!ignore) {
          setError(apiError?.response?.data?.message || "Failed to fetch business planning summary.");
          setRawItems([]);
        }
      } finally {
        if (!ignore) setLoading(false);
      }
    };
    fetchBusinessPlanningSummary();
    return () => { ignore = true; };
  }, [location.state, segmentKey, selectedPeriod, selectedYear]);

  useEffect(() => {
    if (!selectedCategory || !location.state) {
      setClassificationData([]); setClassificationError(""); return;
    }
    let ignore = false;
    const fetchClassificationData = async () => {
      setClassificationLoading(true); setClassificationError(""); setClassificationData([]);
      try {
        const response = await axiosInstance.post("/eda/fixed-cost-classification", {
          Months: selectedPeriod?.months?.join(","),
          Year: selectedYear,
          Category: selectedCategory,
          Type: segmentKey.toUpperCase(),
        });
        if (!ignore && response?.data?.status) {
          setClassificationData(response.data.items || []);
        } else if (!ignore) {
          setClassificationError(response?.data?.message || "Failed to fetch classification data.");
        }
      } catch (apiError) {
        if (!ignore) {
          setClassificationError(apiError?.response?.data?.message || "Failed to fetch classification data.");
        }
      } finally {
        if (!ignore) setClassificationLoading(false);
      }
    };
    fetchClassificationData();
    return () => { ignore = true; };
  }, [selectedCategory, selectedPeriod, selectedYear, location.state, segmentKey]);

  return {
    dashboard, selectedProcess, segment, segmentKey,
    periodOptions, selectedPeriod, setSelectedPeriod,
    selectedYear, setSelectedYear,
    loading, error,
    rows, monthColumns, groupRows, groupMonthColumns, stateRows, stateMonthColumns,
    selectedCategory, handleRowClick,
    classificationLoading, classificationError, classificationData,
    transformedClassificationData, classificationMonths, segmentColumns
  };
};
